import React, { useMemo, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, Lock } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
type MarketCode = 'AU' | 'SG' | 'US';
type LockedCode = 'EU' | 'NZ';
type CompareVerdict = 'Permitted' | 'Conditions' | 'Exceeds limit' | 'Unknown' | 'Not applicable';
type CompareRow = {
  name: string;
  amount: string;
  verdicts: Record<MarketCode, CompareVerdict>;
  limits?: Record<MarketCode, string>;
  limitNote?: string;
};
const marketMeta: Record<MarketCode, {
  name: string;
  classification: string;
}> = {
  AU: {
    name: 'Australia',
    classification: 'Listed medicine (AUST L)'
  },
  SG: {
    name: 'Singapore',
    classification: 'Health supplement'
  },
  US: {
    name: 'United States',
    classification: 'Dietary supplement (DSHEA)'
  }
};
const lockedMarkets: LockedCode[] = ['EU', 'NZ'];
const selectableMarkets: MarketCode[] = ['AU', 'SG', 'US'];
const lockedTooltip = 'No assessment yet — create a dossier for this market to compare';
const summaries: Record<MarketCode, string> = {
  AU: '4 permitted · 4 with conditions · 3 exceeds limit · 1 verification required',
  SG: '3 permitted · 8 with conditions · 1 exceeds limit',
  US: 'No assessment'
};
const rows: CompareRow[] = [{
  name: 'Orange juice concentrate',
  amount: '18.2%',
  verdicts: {
    AU: 'Exceeds limit',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}, {
  name: 'Mandarin orange juice concentrate',
  amount: '10.9%',
  verdicts: {
    AU: 'Exceeds limit',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}, {
  name: 'Natural orange flavour',
  amount: '3.2%',
  verdicts: {
    AU: 'Unknown',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}, {
  name: 'Stevia rebaudiana',
  amount: '0.5%',
  verdicts: {
    AU: 'Permitted',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}, {
  name: 'Resistant maltodextrin, corn',
  amount: '34.3%',
  verdicts: {
    AU: 'Conditions',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}, {
  name: 'Silicon dioxide',
  amount: '1.0%',
  verdicts: {
    AU: 'Permitted',
    SG: 'Permitted',
    US: 'Not applicable'
  }
}, {
  name: 'Vitamin C',
  amount: '550 mg',
  verdicts: {
    AU: 'Permitted',
    SG: 'Permitted',
    US: 'Not applicable'
  }
}, {
  name: 'Zinc sulfate',
  amount: '15 mg elemental',
  verdicts: {
    AU: 'Conditions',
    SG: 'Conditions',
    US: 'Not applicable'
  },
  limits: {
    AU: 'Maximum 25 mg elemental zinc per day',
    SG: 'Maximum 15 mg elemental zinc per day',
    US: 'No assessment'
  },
  limitNote: 'Same verdict, different basis — within limit in Australia, at the limit in Singapore.'
}, {
  name: 'Vitamin A, retinol acetate',
  amount: '3,600 µg RE',
  verdicts: {
    AU: 'Exceeds limit',
    SG: 'Exceeds limit',
    US: 'Not applicable'
  },
  limits: {
    AU: 'Maximum 3,000 µg RE per day',
    SG: 'Maximum 1,500 µg RE per day',
    US: 'No assessment'
  },
  limitNote: 'One reformulation to ≤1,500 µg RE clears both markets.'
}, {
  name: 'Canthaxanthin',
  amount: '0.325%',
  verdicts: {
    AU: 'Conditions',
    SG: 'Conditions',
    US: 'Not applicable'
  }
}];
interface FormulaCompareViewProps {
  productName: string;
  onBack: () => void;
  variant?: 'supplement' | 'food';
}
export function FormulaCompareView({
  productName,
  onBack,
  variant = 'supplement'
}: FormulaCompareViewProps) {
  const [selected, setSelected] = useState<MarketCode[]>(['AU', 'SG']);
  const [differencesOnly, setDifferencesOnly] = useState(false);
  const [activeCell, setActiveCell] = useState<{
    row: string;
    market: MarketCode;
  } | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const toggleMarket = (market: MarketCode) => {
    setActiveCell(null);
    setSelected(current => {
      if (current.includes(market)) {
        return current.length === 1 ? current : current.filter(code => code !== market);
      }
      if (current.length >= 3) return current;
      return selectableMarkets.filter(code => current.includes(code) || code === market);
    });
  };
  const visibleRows = useMemo(() => {
    if (!differencesOnly) return rows;
    return rows.filter(row => new Set(selected.map(market => row.verdicts[market])).size > 1);
  }, [differencesOnly, selected]);
  const gridTemplate = {
    gridTemplateColumns: `minmax(220px, 1.4fr) repeat(${selected.length}, minmax(150px, 1fr))`
  };
  return <div className="flex flex-1 flex-col overflow-hidden bg-[#fafbf8]" data-id="element-451" data-mp="93n6abxik89n">
      <header className="border-b border-[#dce3da] bg-white px-6 py-4 lg:px-10" data-id="element-452" data-mp="91si8oqy401c">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-forest-green transition hover:underline" data-id="element-453" data-mp="440v0818x23h">
          <ArrowLeft className="h-4 w-4" data-id="element-454" data-mp="3qb8vavxb6isn" />
          Back to Formula
        </button>
        <p className="mt-3 text-sm text-[#718174]" data-id="element-455" data-mp="3ma5heeytmwu2">{productName}</p>
        <h1 className="mt-0.5 font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-456" data-mp="3maf67z84qxy6">Ingredient comparison</h1>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-10" data-id="element-457" data-mp="1kiik5voc65p5">
        <div className="mx-auto max-w-7xl" data-id="element-458" data-mp="1kgpmh5vpavta">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-459" data-mp="1fjfq169w9jvy">
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Markets to compare" data-id="element-460" data-mp="1n3q8wzjrpx0m">
              {selectableMarkets.map(market => {
              const isSelected = selected.includes(market);
              const atLimit = !isSelected && selected.length >= 3;
              return <button key={market} type="button" onClick={() => toggleMarket(market)} disabled={atLimit} aria-pressed={isSelected} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold transition ${isSelected ? 'border-forest-green bg-forest-green text-white' : atLimit ? 'cursor-not-allowed border-[#dce3da] bg-white text-[#b0b9b1]' : 'border-forest-green/45 bg-white text-forest-green hover:bg-[#f3f8f1]'}`} data-id="element-461" data-mp="21qt8rcvkchr4">
                    {isSelected && <Check className="h-3 w-3" data-id="element-462" data-mp="pfz3tprshan4" />}
                    {market}
                  </button>;
            })}
              {lockedMarkets.map(market => <Tooltip key={market} content={lockedTooltip} data-id="element-463" data-mp="yg4a1wl8y2zp">
                  <span className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-dashed border-[#c3ccc4] bg-[#f7f8f6] px-2.5 py-1 font-mono text-[11px] font-semibold text-[#8b968d]" data-id="element-464" data-mp="ygx9echvlq7t">
                    <Lock className="h-3 w-3" data-id="element-465" data-mp="6n4zdprsl0x5" />
                    {market}
                  </span>
                </Tooltip>)}
              <span className="ml-1 text-xs text-[#8b968d]" data-id="element-466" data-mp="fpe7ya3i2k7w">Maximum three markets</span>
            </div>
            <label className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-evergreen" data-id="element-467" data-mp="3o4oafjbaxrg7">
              <input type="checkbox" checked={differencesOnly} onChange={event => setDifferencesOnly(event.target.checked)} className="h-4 w-4 rounded border-[#c3ccc4] text-forest-green focus:ring-forest-green" data-id="element-468" data-mp="3j5bi4j1nvs68" />
              Differences only
            </label>
          </div>

          <section className="mt-5 overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-469" data-mp="1umqop1lccpjv">
            <div className="overflow-x-auto" data-id="element-470" data-mp="2icl0dftxc82j">
              <div className="min-w-[680px]" data-id="element-471" data-mp="2icta4yur2x9i">
                <div className="grid items-center border-b border-[#e5ebe3] bg-[#fbfdf9]" style={gridTemplate} data-id="element-472" data-mp="2mdmx9q0dc81c">
                  <p className="px-5 py-4 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-473" data-mp="28endavnswhbi">Classification</p>
                  {selected.map(market => <div key={market} className="px-5 py-4" data-id="element-474" data-mp="wz8d7j0ap9c6">
                      <p className="text-sm font-medium text-evergreen" data-id="element-475" data-mp="x11mz767g9oe">{market === 'SG' ? `Singapore (${variant === 'food' ? 'SFA' : 'HSA'})` : marketMeta[market].name}</p>
                      <p className="mt-0.5 text-xs text-[#718174]" data-id="element-476" data-mp="1tme8f4ev4cg0">{marketMeta[market].classification}</p>
                    </div>)}
                </div>
                <p className="border-b border-[#e5ebe3] px-5 py-3 text-xs leading-relaxed text-[#718174]" data-id="element-477" data-mp="1jmx68wytkgos">
                  Australia and Singapore both restrict vitamin A and zinc, at different thresholds. Expand any cell to see the limit that applies in that market.
                </p>

                <div className="divide-y divide-[#edf0eb]" data-id="element-478" data-mp="27dax4k5o49hs">
                  {visibleRows.length > 0 ? visibleRows.map(row => {
                  const isExpanded = expandedRow === row.name;
                  return <div key={row.name} data-id="element-479" data-mp="1xmy8afqtlu5u">
                          <div className="grid items-center" style={gridTemplate} data-id="element-480" data-mp="2v35dvct3yi7b">
                            <div className="px-5 py-4" data-id="element-481" data-mp="2qbvlw5k372s9">
                              {row.limits ? <button type="button" onClick={() => setExpandedRow(current => current === row.name ? null : row.name)} aria-expanded={isExpanded} className="flex items-center gap-1.5 text-left" data-id="element-482" data-mp="2ykt2kyiu8ybm">
                                  <ChevronDown className={`h-3.5 w-3.5 text-[#8b968d] transition ${isExpanded ? 'rotate-180' : ''}`} data-id="element-483" data-mp="3hjdq6glitlki" />
                                  <span data-id="element-484" data-mp="3lk7gfgmfrcgk">
                                    <span className="block text-sm font-semibold text-evergreen" data-id="element-485" data-mp="3liw9j7xp1ol8">{row.name}</span>
                                    <span className="mt-0.5 block text-xs text-[#8b968d]" data-id="element-486" data-mp="3qe1w88yx77ad">{row.amount}</span>
                                  </span>
                                </button> : <>
                                  <p className="text-sm font-semibold text-evergreen" data-id="element-487" data-mp="94dgf2q5ezll">{row.name}</p>
                                  <p className="mt-0.5 text-xs text-[#8b968d]" data-id="element-488" data-mp="3r7shmz0r9o4e">{row.amount}</p>
                                </>}
                            </div>
                            {selected.map(market => {
                        const isActive = activeCell?.row === row.name && activeCell.market === market;
                        return <div key={market} className="px-5 py-4" data-id="element-489" data-mp="dqt6uhx48hbp">
                                  <button type="button" onClick={() => setActiveCell({
                            row: row.name,
                            market
                          })} className={`rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-green ${isActive ? 'ring-2 ring-forest-green ring-offset-1' : ''}`} data-id="element-490" data-mp="ipmkqbgbeace">
                                    <VerdictPill verdict={row.verdicts[market]} data-id="element-491" data-mp="2tj8noqwd6j4y" />
                                  </button>
                                </div>;
                      })}
                          </div>
                          {isExpanded && row.limits && <div className="border-t border-[#eef2ec] bg-[#f8faf6] px-5 py-4" data-id="element-492" data-mp="2c9o62yx55p37">
                              <div className="grid gap-3 sm:grid-cols-2" data-id="element-493" data-mp="27bmpqlr3o3vf">
                                {selected.map(market => <div key={market} className="rounded-lg border border-[#e1e8df] bg-white px-3 py-2.5" data-id="element-494" data-mp="2zypmb8c7u4j1">
                                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7a897d]" data-id="element-495" data-mp="2uz374tctue7l">{marketMeta[market].name}</p>
                                    <p className="mt-1 text-sm text-[#506355]" data-id="element-496" data-mp="2r03t8j94b740">{row.limits?.[market]}</p>
                                  </div>)}
                              </div>
                              {row.limitNote && <p className="mt-3 text-sm font-medium text-evergreen" data-id="element-497" data-mp="1h0axrbme6eth">{row.limitNote}</p>}
                            </div>}
                        </div>;
                }) : <p className="px-5 py-10 text-center text-sm text-[#718174]" data-id="element-498" data-mp="171lkxx9crkk3">All selected markets agree on every row.</p>}
                </div>

                <div className="grid items-start border-t border-[#e5ebe3] bg-[#fbfdf9]" style={gridTemplate} data-id="element-499" data-mp="y8idc5le21xd">
                  <div className="px-5 py-4" data-id="element-500" data-mp="1311okvwlskz0">
                    <p className="text-sm font-semibold text-evergreen" data-id="element-501" data-mp="obdry7zdaahm">4 of 10 rows differ</p>
                    <p className="mt-1 text-sm font-medium text-amber-700" data-id="element-502" data-mp="tagkisy5uo8p">Both markets reject 1 ingredient</p>
                  </div>
                  {selected.map(market => <p key={market} className="px-5 py-4 font-mono text-xs leading-relaxed text-[#617366]" data-id="element-503" data-mp="fcs9sdxt4xhd">{summaries[market]}</p>)}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-4 rounded-2xl border border-[#dce3da] bg-white px-5 py-4 shadow-sm" aria-live="polite" data-id="element-504" data-mp="3rujk6c6pawps">
            {activeCell ? <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-505" data-mp="9q88bvmv06ch">
                <div data-id="element-506" data-mp="4o0t8x6qga5x">
                  <p className="text-sm font-semibold text-evergreen" data-id="element-507" data-mp="4rwyvm0h7xus">{activeCell.row}</p>
                  <p className="mt-1 text-sm text-[#718174]" data-id="element-508" data-mp="jkqfjarhik11">
                    {marketMeta[activeCell.market].name} · {rows.find(row => row.name === activeCell.row)?.verdicts[activeCell.market]}
                  </p>
                </div>
                <button type="button" className="self-start text-sm font-semibold text-forest-green hover:underline sm:self-auto" data-id="element-509" data-mp="od1gmgxyobv6">
                  Open the {marketMeta[activeCell.market].name} assessment →
                </button>
              </div> : <p className="text-sm text-[#718174]" data-id="element-510" data-mp="xceq39m350fw">Select a cell to see the market-specific detail.</p>}
          </section>
        </div>
      </div>
    </div>;
}
function VerdictPill({
  verdict
}: {
  verdict: CompareVerdict;
}) {
  const style: Record<CompareVerdict, string> = {
    Permitted: 'bg-forest-green/10 text-forest-green',
    Conditions: 'bg-amber-100 text-amber-800',
    'Exceeds limit': 'bg-red-100 text-red-800',
    Unknown: 'bg-slate-100 text-slate-600',
    'Not applicable': 'bg-[#f4f6f3] text-[#9aa59c]'
  };
  const label: Record<CompareVerdict, string> = {
    Permitted: 'Permitted',
    Conditions: 'Permitted with conditions',
    'Exceeds limit': 'Exceeds limit',
    Unknown: 'Verification required',
    'Not applicable': 'Not applicable'
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${style[verdict]}`} data-id="element-511" data-mp="1cf6hci5id041">{label[verdict]}</span>;
}